import { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Button, Box, TextField,
  FormControl, FormLabel, Slider, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { surveyApi } from '../utils/api';
import { SurveyDefinition, PostSurveyResponses, SurveyQuestion } from '../types';

export default function PostSurveyPage() {
  const { updateStatus } = useSession();

  const { data: surveyTypes } = useQuery({
    queryKey: ['survey', 'for-assessment'],
    queryFn: () => surveyApi.getSurveyTypes(),
  });

  // Auto-skip if no post survey is linked to this assessment
  useEffect(() => {
    if (surveyTypes && !surveyTypes.survey_types.includes('post')) {
      updateStatus('complete');
    }
  }, [surveyTypes, updateStatus]);

  const { data: definition } = useQuery<SurveyDefinition>({
    queryKey: ['survey', 'definition', 'post'],
    queryFn: () => surveyApi.getDefinition('post'),
    enabled: surveyTypes?.survey_types.includes('post') ?? false,
  });
  const [responses, setResponses] = useState<PostSurveyResponses>({});
  const [error, setError] = useState('');

  const handleChange = (questionId: string, value: string | number) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const submitMutation = useMutation({
    mutationFn: (r: PostSurveyResponses) => surveyApi.submitPostSurvey(r),
    onSuccess: () => updateStatus('complete'),
    onError: () => setError('Failed to submit survey. Please try again.'),
  });

  const handleSubmit = () => {
    setError('');
    submitMutation.mutate(responses);
  };

  const handleBack = async () => {
    await updateStatus('feedback');
  };

  const renderQuestion = (question: SurveyQuestion, index: number) => {
    const questionNumber = index + 1;
    
    if (question.type === 'scale') {
      return (
        <FormControl  key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
          <FormLabel id={`question-${question.id}-label`}>
            {questionNumber}. {question.question}
          </FormLabel>
          <Box sx={{ px: 2, pt: 2 }}>
            <Slider
              value={responses[question.id as keyof PostSurveyResponses] as number || question.min}
              onChange={(_, value) => handleChange(question.id, value as number)}
              min={question.min}
              max={question.max}
              step={1}
              marks={[
                { value: question.min!, label: question.min_label },
                { value: question.max!, label: question.max_label },
              ]}
              valueLabelDisplay="auto"
              aria-labelledby={`question-${question.id}-label`}
            />
          </Box>
        </FormControl>
      );
    }

    if (question.type === 'textarea') {
      return (
        <FormControl  key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
          <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
            {questionNumber}. {question.question}
          </FormLabel>
          <TextField
            value={responses[question.id as keyof PostSurveyResponses] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            multiline
            rows={3}
            variant="outlined"
            inputProps={{
              'aria-labelledby': `question-${question.id}-label`,
            }}
          />
        </FormControl>
      );
    }

    return null;
  };

  if (!definition) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading survey...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          {definition.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Estimated time: {definition.estimated_time}
        </Typography>

        <Typography variant="body2" paragraph sx={{fontSize: '1rem', mb: 5 }}>
          {definition.instructions}
        </Typography>

        <Box component="form" noValidate>
          {definition.questions.map((question, index) => renderQuestion(question, index))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {error}
          </Alert>
        )}

        <Box display="flex" justifyContent="space-between" sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            disabled={submitMutation.isPending}
            startIcon={<ArrowBackIcon />}
            sx={{ px: 4 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            sx={{ px: 6 }}
          >
            {submitMutation.isPending ? 'Submitting...' : 'Continue'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
